import * as moment from "moment-timezone";
import * as React from "react";
import styled, { css } from "styled-components";
import * as yup from "yup";
import { toast } from "react-toastify";
import {
  useHistory,
  useRouteMatch
} from "react-router-dom";
import { useMappedState } from "redux-react-hook";

import { useWindowSize } from "src/library/hooks/useWindowSize";
import { Button } from "src/library/components/Button";
import { DayRangePicker } from "src/library/components/DayRangePicker";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { Modal } from "src/library/components/Modal";
import {
  CheckboxLabel,
  CheckboxStyled,
  FormRow,
  Label,
  Radio
} from "src/library/components/Forms";
import Input from "src/library/components/Forms/Input";
import { Icons } from "src/library/components/Icon/types";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { Tooltip } from "src/library/components/Tooltip";
import { ComponentDates } from "src/library/components/DayRangePicker/DayRangePicker";
import { useCurrentUser } from "src/core/users/hooks";
import useCache from "src/core/shared/hooks/useCache";
import { GLOBAL_SETTINGS_CACHE_KEY } from "src/core/shared/api/globalSettings.api";
import { IStore } from "src/core/shared/store";

import {
  useShutdownsApi,
  IShutdown
} from "../api/shutdown.api";
import { useDropShutdowns } from "../hooks/useDropShutdowns";

interface IScheduleFormProps {
  initialShutdown: IShutdown | null;
  dropId?: number;
  disableExtendLeadTimes?: boolean;
  forceCancelOrders?: boolean;
}

interface UrlParams {
  dropId?: string;
  customerId?: string;
  shutdownId?: string;
}

const ShutdownForm: React.ReactFCWithChildren<IScheduleFormProps> = props => {
  const {
    initialShutdown,
    dropId,
    forceCancelOrders
  } = props;

  const history = useHistory();
  const { removeItemByKey } = useCache();
  const match = useRouteMatch<UrlParams>();
  const [ { currentRole } ] = useCurrentUser();
  const installedModules = useMappedState(({ global }: IStore) => global.settings?.installedModules ?? []);
  const [ showShutdownWarning, setShowShutdownWarning ] = React.useState(false);
  const [ createShutdownRes, createShutdownReq ] = useShutdownsApi("CREATE_SHUTDOWN");
  const [ updateShutdownRes, updateShutdownReq ] = useShutdownsApi("UPDATE_SHUTDOWN");
  const { width } = useWindowSize();
  const { checkShutdownDate, shutdownDates } = useDropShutdowns();

  const [ validation, setValidation ] = React.useState<{ error: boolean; message: string }>({
    error: false,
    message: ""
  });

  const dateFormat = width && width < 786 ? "DD/MM/YY" : "ddd DD MMM YYYY";

  const [ form, setForm ] = React.useState<Partial<IShutdown>>(initialShutdown ? {
    ...initialShutdown,
    startDate: moment.utc(initialShutdown.startDate).toDate(),
    endDate: moment.utc(initialShutdown.endDate).toDate(),
    options: initialShutdown.options ? initialShutdown.options : { orderTypesToAllow: [ ] },
    ...(forceCancelOrders && { cancelExistingOrders: true })
  } : {
    extendLeadTime: false,
    cancelExistingOrders: true,
    options: { orderTypesToAllow: [ ] }
  });

  // Derive values from form state
  const allowAdhocOrders = form.options?.orderTypesToAllow?.includes("adhoc") ?? false;
  const allowScheduledOrders = form.options?.orderTypesToAllow?.includes("scheduled") ?? false;
  const allowBulkOrders = form.options?.orderTypesToAllow?.includes("bulk") ?? false;
  const extendLeadTime = form.extendLeadTime ?? false;

  const handleFormChange = React.useCallback(
    (name: keyof IShutdown, value: any) => {
      setValidation({
        error: false,
        message: ""
      });

      if (name === "cancelExistingOrders" || name === "extendLeadTime") {
        setForm(currentForm => ({
          ...currentForm,
          [ name ]: value === "1" ? true : false
        }));
      } else if (name === "options") {
        const options = { ...form.options };

        options.orderTypesToAllow = value;

        setForm({
          ...form,
          options
        });
      } else {
        setForm(currentForm => ({
          ...currentForm,
          [ name ]: value
        }));
      }
    },
    [ form ]
  );

  // performs the relevant api request based on id
  const saveShudown = React.useCallback(() => {
    const reqParams: Partial<IShutdown> = {
      ...form,
      branchId: dropId
    };

    if (form.id) {
      updateShutdownReq({
        shutdownId: form.id,
        shutdownUpdate: reqParams
      });
    } else {
      createShutdownReq(reqParams as IShutdown);
    }
  }, [
    form,
    createShutdownReq,
    updateShutdownReq,
    dropId
  ]);

  // handles the submit button event
  const handleSubmit = React.useCallback(() => {
    shutdownSchema
      .validate(form)
      .then(() => {
        if (form.cancelExistingOrders) {
          setShowShutdownWarning(true);
        } else {
          saveShudown();
        }
      })
      .catch(err => {
        setValidation({
          error: true,
          message: err.errors
        });
      });
  }, [ form, saveShudown ]);

  React.useEffect(() => {
    if (createShutdownRes.data || updateShutdownRes.data) {
      toast.success("Saved successfully");
      removeItemByKey(GLOBAL_SETTINGS_CACHE_KEY);

      if (createShutdownRes.data?.branchId || updateShutdownRes.data?.branchId) {
        if (currentRole?.type === "admin") {
          if (match.params.customerId && match.params.dropId) {
            history.push(`/admin/customers/${match.params.customerId}/drops/${match.params.dropId}/shutdowns`);
          }
        } else {
          history.push("/shutdowns");
        }
      } else {
        history.push("/platform/shutdowns");
      }
    }

    if (createShutdownRes.error || updateShutdownRes.error) {
      toast.error((createShutdownRes.error ? createShutdownRes.error.message : null) || (updateShutdownRes.error ? updateShutdownRes.error.message : "Error"));
    }
  }, [
    createShutdownRes,
    currentRole?.type,
    history,
    match?.params.customerId,
    match?.params.dropId,
    updateShutdownRes,
    removeItemByKey
  ]);

  /** Modifier adds a dot to the calendar to show an existing shutdown exists */
  const modifiers = React.useMemo(() => ({
    withActiveDate: (day: Date) => {
      const shutdownDetails = checkShutdownDate(day);

      return !!shutdownDetails?.shutdown.branchId;
    }
  }), [ checkShutdownDate ]);

  /** Returns a list of dates that should be disabled */
  const disableShutdownDates: ComponentDates[] = React.useMemo(() => {
    const dates: ComponentDates[] = shutdownDates.filter(sd => {
      if (sd.shutdown.id === initialShutdown?.id) {
        return false;
      }

      if (dropId) {
        return !!sd.shutdown.branchId;
      } else {
        return !sd.shutdown.branchId;
      }
    }).map(sd => {
      const dateDetails: ComponentDates = {
        date: moment.utc(sd.date, "YYYY-MM-DD").toDate(),
        component: (
          <>
            <Text>
              This date is already blocked for ordering due to another holiday:
              {" "}

              {sd.shutdown.reason}
            </Text>
          </>
        )
      };

      return dateDetails;
    });

    return dates;
  }, [
    dropId,
    initialShutdown?.id,
    shutdownDates
  ]);

  const getSelectedOrderTypes = React.useCallback((adhocChecked: boolean, scheduledChecked: boolean, bulkChecked?: boolean) => {
    const selectedTypes = [];

    if (bulkChecked) {
      selectedTypes.push("bulk");
    }

    if (adhocChecked) {
      selectedTypes.push("adhoc");
    }

    if (scheduledChecked) {
      selectedTypes.push("scheduled");
    }

    return selectedTypes;
  }, []);

  const shutdownOrHoliday = React.useMemo(() => {
    if (dropId) {
      return "holiday";
    } else {
      return "shutdown";
    }
  }, [ dropId ]);

  return (
    <FormContainer>
      <Modal
        backgroundAction={() => setShowShutdownWarning(false)}
        show={showShutdownWarning}
        title="Are you sure?"
        small={false}
        body="This action may cancel existing orders and cannot be undone."
        actions={[
          {
            name: "No, cancel",
            $buttonType: "secondary",
            action: () => setShowShutdownWarning(false),
            disabled: createShutdownRes.pending || updateShutdownRes.pending
          },
          {
            name: "I confirm",
            $buttonType: "primary",
            action: () => saveShudown(),
            disabled: createShutdownRes.pending || updateShutdownRes.pending
          }
        ]}
      />

      {validation.error && (
        <ValidationContainer>
          <Icon
            $fillColor="error"
            icon={Icons.ERRORCIRCLE}
          />

          {validation.message}
        </ValidationContainer>
      )}

      <FormRow>
        <Input
          as="input"
          handleChange={e => handleFormChange("reason", e.target.value)}
          name="reason"
          label={`Reason for ${shutdownOrHoliday}`}
          inputType="text"
          defaultValue={initialShutdown ? initialShutdown.reason : ""}
        />
      </FormRow>

      <FormRow>
        {(currentRole?.type === "admin") && (
          <div>

            <div
              data-tip
              data-for="order-types-help"
              className="label-with-help"
            >
              <Label
                label={`Which order types would you like this ${shutdownOrHoliday} to apply to?`}
                for="orderType"
              />

              <Icon
                icon={Icons.HELP}
                size={10}
              />
            </div>

            <Tooltip
              id="order-types-help"
              type="dark"
              place="top"
              getContent={() => {
                return (
                  <Text color="white">
                    Customers will be able to place orders of any types not selected below. Note - this option is not available for customers and must be set by an admin user.
                  </Text>
                );
              }}
            />

            <div className="order-type">
              <CheckboxLabel
                className="allow-adhoc-wrapper"
                for="adhoc"
                label="Adhoc orders"
              >

                <CheckboxStyled
                  checked={!allowAdhocOrders}
                  onChange={e => {
                    handleFormChange("options", getSelectedOrderTypes(!e.target.checked, allowScheduledOrders, allowBulkOrders));
                  }}
                  name="adhoc"
                />
              </CheckboxLabel>
            </div>

            <div className="order-type">
              <CheckboxLabel
                className="allow-scheduled-wrapper"
                for="scheduled"
                label="Standing orders"
              >

                <CheckboxStyled
                  checked={!allowScheduledOrders}
                  onChange={e => {
                    handleFormChange("options", getSelectedOrderTypes(allowAdhocOrders, !e.target.checked, allowBulkOrders));
                  }}
                  name="scheduled"
                />
              </CheckboxLabel>
            </div>

            {installedModules?.includes("order-import") && (
              <div className="order-type">
                <CheckboxLabel
                  className="allow-bulk-wrapper"
                  for="bulk"
                  label="Order imports (EDI)"
                >

                  <CheckboxStyled
                    checked={!allowBulkOrders}
                    onChange={e => {
                      handleFormChange("options", getSelectedOrderTypes(allowAdhocOrders, allowScheduledOrders, !e.target.checked));
                    }}
                    name="bulk"
                  />
                </CheckboxLabel>
              </div>
            )}

          </div>
        )}
      </FormRow>

      <FormRow>

        {!props.disableExtendLeadTimes && (
          <>
            <div
              data-tip
              data-for="extend-lead-time-help"
              className="label-with-help"
            >
              <Label
                label="Extend Lead Times"
                for="extendLeadTime"
              />

              <Icon
                icon={Icons.HELP}
                size={10}
              />
            </div>

            <CheckboxStyled
              name="extendLeadTime"
              onChange={e => {
                handleFormChange("extendLeadTime", e.target.checked ? "1" : "0");
              }}
              checked={extendLeadTime}
            />

            <Tooltip
              id="extend-lead-time-help"
              type="dark"
              place="top"
              getContent={() => {
                return (
                  <Text color="white">
                    {`This option will extend lead times by the number of days in the ${shutdownOrHoliday} for orders where the lead time would be within this shutdown.`}
                  </Text>
                );
              }}
            />
          </>
        )}
      </FormRow>

      <FormRow style={{ position: "relative" }}>
        <Label
          label="Select start and end date"
          for="date-range"
        />

        <SelectedDates>
          <Icon
            size={18}
            icon={Icons.CALENDAR}
            $fillColor="midGrey"
          />

          <Text>
            {form && form.startDate && form.endDate ?
              `${moment.utc(form.startDate).format(dateFormat)} - ${moment.utc(form.endDate).format(dateFormat)}` :
              "Dates"}
          </Text>
        </SelectedDates>

        <DayRangePickerWrapper>
          <DayRangePicker
            maxDate={moment.utc().add(2, "years").toDate()}
            date={(form && form.startDate) || new Date()}
            minDate={new Date()}
            modifiers={modifiers}
            disabledDates={disableShutdownDates}
            initialDateRange={initialShutdown ? {
              from: moment.utc(initialShutdown.startDate).startOf("day").toDate(),
              to: moment.utc(initialShutdown.endDate).endOf("day").toDate()
            } : null}
            handleDateChange={({ from, to }) => {
              if (from) {
                handleFormChange("startDate", from);
              }

              if (to) {
                handleFormChange("endDate", to);
              }
            }}
          />
        </DayRangePickerWrapper>

      </FormRow>

      <FormRow>
        {currentRole?.type === "customer" ? (
          <>
            <ErrorNotice
              type="warn"
              message={`This ${shutdownOrHoliday} will automatically cancel existing orders and block creation of new orders that fall within the selected date range.`}
            />
          </>
        ) : (
          <>
            <Label
              label="Cancel or block orders"
              for="cancelExistingOrders"
            />

            <ErrorNotice
              type="warn"
              message="If you select to cancel orders, when this form is saved the platform will cancel all orders that fall within the date range selected"
            />

            <Radio
              options={[
                {
                  text: "Cancel and block orders on this date",
                  value: "1",
                  checked: form.cancelExistingOrders === true
                },
                {
                  text: "Block orders only",
                  value: "0",
                  checked: form.cancelExistingOrders === false
                }
              ]}
              handleChange={e => {
                handleFormChange("cancelExistingOrders", e.target.value);
              }}
              name="cancelExistingOrders"
              label=""
              value={typeof form.cancelExistingOrders === "boolean" ? form.cancelExistingOrders ? "1" : "0" : ""}
            />
          </>
        )}
      </FormRow>

      <SubmitContainer>
        <Button
          loading={createShutdownRes.pending || updateShutdownRes.pending}
          $buttonType="primary"
          onClick={() => handleSubmit()}
        >
          <>
            {initialShutdown ? `Update ${shutdownOrHoliday}` : `Create ${shutdownOrHoliday}`}
          </>
        </Button>
      </SubmitContainer>
    </FormContainer>
  );
};

const ValidationContainer = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
    color: ${theme.colors.error};
    display: flex;
    align-items: center;

    svg {
      margin-right: ${theme.spacing.padding.sm};
    }
  `}
`;

const DayRangePickerWrapper = styled.div`
 .DayPicker {
  width: 225px;
 }
`;

const SubmitContainer = styled.div`

  ${({ theme }) => css`
    padding-top: ${theme.spacing.padding.md};
    > * {
      margin: 0 ${theme.spacing.padding.xs};
    }

    > button:first-child {
      margin: 0 -${theme.spacing.padding.xs};
    }
  `}
`;

const FormLine = styled.div`

  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md} 0;
  `}
`;

const FormContainer = styled.div`
  .label-with-help {
    display: flex;
    flex-direction: row;
  }
  &.suspended ${FormLine} {
    opacity: 0.5;
    pointer-events: none;
  }
`;

const SelectedDates = styled.div`
  display: flex;
  flex-direction: row;
  > span {
    ${({ theme }) => css`
      margin-left: ${theme.spacing.padding.xs};
    `}
  }
`;

export const shutdownSchema = yup.object().shape({
  reason: yup.string().required(),
  cancelExistingOrders: yup.bool().required(),
  extendLeadTime: yup.bool(),
  options: yup.object().shape({ orderTypesToAllow: yup.array().of(yup.string()) }),
  startDate: yup
    .date()
    .label("Start date")
    .required(),
  endDate: yup
    .date()
    .label("End date")
    .required()
    .when("startDate", (startDate: Date, schema: yup.DateSchema) => {
      if (startDate) {
        return schema.min(startDate);
      } else {
        return schema;
      }
    })
});

export default ShutdownForm;
